import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, MenuItem, Button, Alert, IconButton, Chip, Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import { superAdminApi, saClient, SubscriptionPlan } from '../../services/superAdminApi';
import TextField from '../../components/common/TextField';

export const SATenantEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState<any[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  const load = async () => {
    try {
      const resp = await superAdminApi.getTenant(Number(id));
      setTenant(resp.data);
      const doms = await saClient.get(`/super-admin/tenants/${id}/domains`);
      setDomains(doms.data.data || []);
    } catch (e: any) {
      setError('No se pudo cargar el tenant');
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const resp = await superAdminApi.listPlans();
        setPlans(resp.data || []);
      } catch {
        // ignore
      }
    })();
  }, [id]);

  useEffect(() => {
    if (tenant && !tenant.subscriptionPlanId && plans.length) {
      const match = plans.find((p) => p.name === tenant.plan);
      if (match) {
        setTenant({ ...tenant, subscriptionPlanId: match.id, plan: match.name });
      }
    }
  }, [tenant, plans]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const planName = plans.find((p) => p.id === tenant.subscriptionPlanId)?.name || tenant.plan;
      const payload = {
        name: tenant.name,
        adminEmail: tenant.adminEmail,
        adminPhone: tenant.adminPhone,
        address: tenant.address,
        status: tenant.status,
        subscriptionActive: tenant.subscriptionActive,
        billingPlan: planName,
        subscriptionPlanId: tenant.subscriptionPlanId,
        maxProducts: tenant.maxProducts,
        maxChannels: tenant.maxChannels,
        maxUsers: tenant.maxUsers,
        subscriptionEndsAt: tenant.subscriptionEndsAt,
        demoEndsAt: tenant.demoEndsAt,
        primaryColor: tenant.primaryColor,
        secondaryColor: tenant.secondaryColor,
      };
      const resp = await superAdminApi.updateTenant(Number(id), payload);
      if (resp.success) navigate('/super-admin/tenants');
      else setError(resp.message || 'Error al guardar');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return null;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Editar Tenant #{tenant.id}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Nombre" value={tenant.name} onChange={(e) => setTenant({ ...tenant, name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Estado" value={tenant.status}
              onChange={(e) => setTenant({ ...tenant, status: e.target.value })}>
              {['Active','Suspended','Cancelled','Demo','Expired','PendingPayment'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Plan" value={plans.length ? (tenant.subscriptionPlanId ?? '') : tenant.plan}
              onChange={(e) => {
                if (plans.length) {
                  const planId = Number(e.target.value);
                  const planName = plans.find((p) => p.id === planId)?.name || tenant.plan;
                  setTenant({ ...tenant, subscriptionPlanId: planId, plan: planName });
                } else {
                  setTenant({ ...tenant, plan: e.target.value, subscriptionPlanId: undefined });
                }
              }}>
              {(plans.length ? plans.map(p => ({ value: p.id, label: p.name })) : ['Starter','Growth','Scale','Enterprise'].map(p => ({ value: p, label: p }))).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Admin Email" value={tenant.adminEmail || ''} onChange={(e) => setTenant({ ...tenant, adminEmail: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Admin Teléfono" value={tenant.adminPhone || ''} onChange={(e) => setTenant({ ...tenant, adminPhone: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Dirección" value={tenant.address || ''} onChange={(e) => setTenant({ ...tenant, address: e.target.value })} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Max Productos" type="number" value={tenant.maxProducts} onChange={(e) => setTenant({ ...tenant, maxProducts: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Max Canales" type="number" value={tenant.maxChannels} onChange={(e) => setTenant({ ...tenant, maxChannels: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Max Usuarios" type="number" value={tenant.maxUsers} onChange={(e) => setTenant({ ...tenant, maxUsers: Number(e.target.value) })} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Fin Demo (UTC)" type="datetime-local" value={tenant.demoEndsAt ? tenant.demoEndsAt.substring(0,19) : ''}
              onChange={(e) => setTenant({ ...tenant, demoEndsAt: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Fin Suscripción (UTC)" type="datetime-local" value={tenant.subscriptionEndsAt ? tenant.subscriptionEndsAt.substring(0,19) : ''}
              onChange={(e) => setTenant({ ...tenant, subscriptionEndsAt: e.target.value })} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Color Primario" value={tenant.primaryColor || ''} onChange={(e) => setTenant({ ...tenant, primaryColor: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Color Secundario" value={tenant.secondaryColor || ''} onChange={(e) => setTenant({ ...tenant, secondaryColor: e.target.value })} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate('/super-admin/tenants')}>Cancelar</Button>
              <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ height: 16 }} />
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Dominios</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Hostname" placeholder="app.togo.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
          </Grid>
          <Grid item xs="auto">
            <Button variant="contained" onClick={async () => {
              if (!newDomain) return;
              await saClient.post(`/super-admin/tenants/${id}/domains`, { hostname: newDomain, isPrimary: domains.length === 0 });
              setNewDomain('');
              await load();
            }}>Agregar</Button>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {domains.map((d) => (
            <Chip key={d.id} label={d.hostname} color={d.isPrimary ? 'primary' : 'default'}
              onDelete={async () => { await saClient.delete(`/super-admin/tenants/${id}/domains/${d.id}`); await load(); }}
              deleteIcon={<DeleteIcon />}
              icon={d.isPrimary ? <StarIcon /> : undefined}
              onClick={async () => { await saClient.post(`/super-admin/tenants/${id}/domains/${d.id}/primary`); await load(); }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
};
